import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ICalendarEvent, IUpdateEvent } from 'app/interfaces/event.interface';
import {
	ITaxonomy,
	ITaxonomyGroupContext,
	ITaxonomyGroupResponse,
} from 'app/interfaces/search.interface';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { TaxonomyService } from 'app/services/taxonomy.service';
import { UserService } from 'app/services/user.service';
import { forkJoin } from 'rxjs';
import { AppointmentService } from 'shared/services/appointment/appointment.service';

@Component({
	selector: 'app-reasons-modal',
	templateUrl: './reasons-modal.component.html',
	styleUrls: ['./reasons-modal.component.scss'],
})
export class ReasonsModalComponent implements OnInit {
	@Input('event') event: ICalendarEvent;

	@Output() onSave: EventEmitter<ITaxonomyGroupResponse[]> = new EventEmitter<
		ITaxonomyGroupResponse[]
	>();
	@Output() onCancel: EventEmitter<any> = new EventEmitter<any>();
	@Output() onClose: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('selectedReasonsList', { static: true })
	private selectedReasonsListElement: ElementRef;

	highlightInputError = false;
	isLoading = false;
	noReasonsFound = false;
	noShowClientCheckbox = false;
	userName = '';
	sessionDate = '';
	sessionStartTime = '';

	allReasons: ITaxonomyGroupResponse[] = [];
	mainAreas: ITaxonomyGroupResponse[] = [];
	noShowClient: ITaxonomy;
	reasonsList: ITaxonomyGroupResponse[] = [];
	selectedMainArea: ITaxonomyGroupResponse;
	selectedReasons: ITaxonomyGroupResponse[] = [];
	searchDebounce: NodeJS.Timeout;
	searchTerm: string;

	constructor(
		private appointmentService: AppointmentService,
		private dateTimeService: DateTimeService,
		private eventsService: EventsService,
		private notifications: ZkNotificationService,
		private taxonomyService: TaxonomyService,
		private translateService: TranslateService,
		private userService: UserService
	) {}

	ngOnInit() {
		this.isLoading = true;

		forkJoin([
			this.taxonomyService.getTaxonomyList('session-outcome'),
			this.taxonomyService.getTaxonomyGroup(
				ITaxonomyGroupContext.SessionReason
			),
		]).subscribe({
			next: ([sessionOutcomes, sessionReasons]) => {
				if (this.event) {
					const { userName, day, contactReason } = this.event;

					this.userName = userName;
					this.sessionDate = this.dateTimeService.getInFullDate(day);
					this.sessionStartTime = this.dateTimeService.getFormatedHours(day);

					this.mainAreas = sessionReasons;
					this.allReasons = sessionReasons.reduce(
						(total, area) => [...total, ...area.children],
						[]
					);

					this.noShowClient = sessionOutcomes.find((item) =>
						item.id.includes('no-show-client')
					);

					if (
						this.noShowClient &&
						contactReason &&
						contactReason.includes(this.noShowClient.id)
					) {
						this.noShowClientCheckbox = true;
					}

					this.selectedReasons =
						contactReason &&
						!contactReason.includes('session-outcome:no-show-client')
							? this.allReasons.filter(
									(reason) =>
										!!contactReason.find(
											(contactReason) => contactReason === reason.taxonomyId
										)
							  )
							: [];
				}

				this.isLoading = false;
			},
			error: (error) => {
				this.isLoading = false;
				const errorMsg = this.translateService.instant('MESSAGES.SAVEDERROR');
				this.notifications.showError(error, errorMsg);
			},
		});
	}

	searchReasons() {
		clearTimeout(this.searchDebounce);

		this.searchDebounce = setTimeout(() => {
			this.selectedMainArea = undefined;
			this.highlightInputError = false;
			this.reasonsList = !!this.searchTerm
				? this.allReasons.filter((item) => {
						return item.name
							.trim()
							.toLowerCase()
							.includes(this.searchTerm.trim().toLowerCase());
				  })
				: [];
			this.noReasonsFound = !!this.searchTerm && !this.reasonsList.length;
		}, 200);
	}

	setSelectedArea(area: ITaxonomyGroupResponse) {
		if (this.noShowClientCheckbox) {
			return;
		}

		this.highlightInputError = false;
		this.selectedMainArea = area;
		this.reasonsList = area.children;
		this.searchTerm = '';
	}

	toggleNoShowClient() {
		if (this.noShowClientCheckbox) {
			this.selectedReasons = [];
			this.reasonsList = [];
			this.selectedMainArea = null;
			this.searchTerm = '';
		}
	}

	toggleReason(reason: ITaxonomyGroupResponse) {
		if (this.noShowClientCheckbox) {
			return;
		}

		if (this.isSelected(reason)) {
			this.selectedReasons = this.selectedReasons.filter(
				(item) => item.id !== reason.id
			);
		} else {
			this.highlightInputError = false;
			this.selectedReasons = [...this.selectedReasons, reason];
			setTimeout(() => {
				this.selectedReasonsListElement.nativeElement.scrollTo(
					0,
					this.selectedReasonsListElement.nativeElement.scrollHeight
				);
			}, 100);
		}
		this.searchTerm = '';
	}

	isSelected(reason: ITaxonomyGroupResponse) {
		return !!this.selectedReasons.find((item) => item.id === reason.id);
	}

	setNoShowState(): void {
		this.appointmentService
			.setNoshow(this.event.id, true, 'no-show:client-no-show')
			.subscribe(
				() => {
					this.eventsService.updateLocalEvent(this.event);
					this.onSave.emit();
					this.isLoading = false;
				},
				({ error }) => {
					this.notifications.showError(error.messageI18n);
					this.isLoading = false;
				}
			);
	}

	async save() {
		const userId: string = await this.userService.getUserId();

		if (this.noShowClientCheckbox) {
			return this.setNoShowState();
		}

		if (!this.selectedReasons.length) {
			this.highlightInputError = true;
			return;
		}

		if (this.event && userId) {
			const upObject: IUpdateEvent = {
				contactReason: this.selectedReasons.map((reason) => reason.taxonomyId),
			};
			this.translateService
				.get([
					'MESSAGES.SAVEDSUCCESSFULLY',
					'MESSAGES.UNSELECTEDREASON',
					'ERRORS.APPOINTMENT.INVALID_DATETIME_FINISH_TITLE',
					'ERRORS.APPOINTMENT.INVALID_DATETIME_FINISH',
				])
				.subscribe((res) => {
					if (this.selectedReasons.length === 0) {
						this.notifications.showError(
							res['MESSAGES.UNSELECTEDREASON'],
							res['MESSAGES.SAVEDERROR']
						);
						return;
					}
					this.isLoading = true;

					this.eventsService
						.confirmEventFinish(this.event.id, upObject)
						.subscribe(
							() => {
								this.notifications.showSuccess(
									res['MESSAGES.SAVEDSUCCESSFULLY']
								);
								// Loaded.
								this.isLoading = false;
								// Update local event
								this.event.contactReason = upObject.contactReason;
								this.eventsService.updateLocalEvent(this.event);
								this.onSave.emit();
							},
							({ error }) => {
								this.isLoading = false;

								if (error.message == 'EventNotFinished') {
									return this.notifications.showError(
										res['ERRORS.APPOINTMENT.INVALID_DATETIME_FINISH'],
										res['ERRORS.APPOINTMENT.INVALID_DATETIME_FINISH_TITLE']
									);
								}

								return this.notifications.showError(error.messageI18n);
							}
						);
				});
		}
	}

	cancel() {
		this.onCancel.emit();
	}

	close() {
		this.onClose.emit();
	}
}
